import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { isGovCloud } from 'core/apiClient';
import { useTeamRetoolIntegrations } from '../hooks/useTeamRetoolIntegrations';
import { useSearchParam } from 'react-use';
import { PricingCalculatorFormV2 } from './PricingCalculator';
import { PricingCalculatorFormValues, PlanType, TenantType } from '../types/pricingCalculator';
import { Box } from '@mui/material';
import { LinearBuffer } from 'components/Progress';
import useNavigationContext from 'hooks/useNavigationContext';
import { getIsTenantEnabled } from 'utils/feature';
import useSearchTeamsByFieldsQuery from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import { TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import {
  useTeamMemberCategoryCountsQuery,
  TeamMemberCategoryCounts,
} from 'StatisticsModule/query/hooks/useTeamMemberCategoryCountsQuery';

// Mapping of short URL param names to form field names
const PARAM_MAP: Record<string, keyof PricingCalculatorFormValues> = {
  cn: 'companyName',
  sd: 'subscriptionDuration',
  rn: 'isRenewal',
  it: 'isItCustomer',
  pt: 'planType',
  nm: 'numberOfMembers',
  pm: 'pricePerMember',
  dm: 'discountOnMemberPrice',
  ic: 'numberOfInternalContractors',
  ng: 'numberOfGuests',
  ec: 'numberOfExternalContractors',
  pf: 'platformFee',
  dp: 'discountOnPlatformFee',
  og: 'numberOfOnboardingGroups',
  of: 'onboardingFee',
  do: 'discountOnOnboardingFee',
  eh: 'additionalEnterpriseSupportHours',
  dd: 'directDatabaseIntegration',
  ddd: 'discountOnDirectDatabase',
  id: 'isolatedDatabase',
  did: 'discountOnIsolatedDatabase',
  idm: 'isolatedDatabaseMigration',
  tt: 'tenantType',
  dt: 'discountOnTenant',
  ia: 'isolatedAi',
  sso: 'securityPackageSso',
  dsso: 'discountOnSecurityPackageSso',
  gc: 'govCloud',
  gcf: 'govCloudFee',
  dgc: 'discountOnGovCloudFee',
  af: 'aiForecasting',
  daf: 'discountOnAiForecasting',
  npr: 'nonPeopleResources',
  dnpr: 'discountOnNonPeopleResources',
  pb: 'profileBuilding',
  dpb: 'discountOnProfileBuilding',
  skt: 'skillsTagging',
  dskt: 'discountOnSkillsTagging',
  ss: 'solutionsSupport',
  ssh: 'solutionsSupportHours',
  wl: 'enableWhiteLabel',
  int: 'selectedIntegrations',
  bft: 'bakIntegrationFetchTimes',
  '4x': 'enable4xDataTransfer',
  im: 'integrationMigration',
  dis: 'discountOnIntegrationSetup',
  dir: 'discountOnIntegrationRecurring',
};

const BOOLEAN_FIELDS = new Set([
  'isRenewal',
  'isItCustomer',
  'directDatabaseIntegration',
  'isolatedDatabase',
  'isolatedDatabaseMigration',
  'isolatedAi',
  'securityPackageSso',
  'govCloud',
  'aiForecasting',
  'nonPeopleResources',
  'profileBuilding',
  'skillsTagging',
  'solutionsSupport',
  'enableWhiteLabel',
  'enable4xDataTransfer',
  'integrationMigration',
]);

const NUMBER_FIELDS = new Set([
  'subscriptionDuration',
  'numberOfMembers',
  'pricePerMember',
  'discountOnMemberPrice',
  'numberOfInternalContractors',
  'numberOfGuests',
  'numberOfExternalContractors',
  'platformFee',
  'discountOnPlatformFee',
  'numberOfOnboardingGroups',
  'onboardingFee',
  'discountOnOnboardingFee',
  'additionalEnterpriseSupportHours',
  'discountOnDirectDatabase',
  'discountOnIsolatedDatabase',
  'discountOnTenant',
  'discountOnSecurityPackageSso',
  'govCloudFee',
  'discountOnGovCloudFee',
  'discountOnAiForecasting',
  'discountOnNonPeopleResources',
  'discountOnProfileBuilding',
  'discountOnSkillsTagging',
  'solutionsSupportHours',
  'bakIntegrationFetchTimes',
  'discountOnIntegrationSetup',
  'discountOnIntegrationRecurring',
]);

/** Assign one raw URL param value onto `values`, coerced by the field's group. */
const applyParsedParam = (
  values: Partial<PricingCalculatorFormValues>,
  field: keyof PricingCalculatorFormValues,
  rawValue: string,
): void => {
  if (field === 'companyName') {
    values.companyName = decodeURIComponent(rawValue);
  } else if (field === 'planType') {
    if (Object.values(PlanType).includes(rawValue as PlanType)) {
      values.planType = rawValue as PlanType;
    }
  } else if (field === 'tenantType') {
    if (Object.values(TenantType).includes(rawValue as TenantType)) {
      values.tenantType = rawValue as TenantType;
    }
  } else if (field === 'selectedIntegrations') {
    values.selectedIntegrations = rawValue ? rawValue.split(',').filter(Boolean) : [];
  } else if (BOOLEAN_FIELDS.has(field)) {
    (values as Record<string, boolean>)[field] = rawValue === '1' || rawValue === 'true';
  } else if (NUMBER_FIELDS.has(field)) {
    const num = parseFloat(rawValue);
    if (!isNaN(num)) {
      (values as Record<string, number>)[field] = num;
    }
  }
};

/** Backward-compat params, applied only where the modern param was absent. */
const applyLegacyParams = (
  searchParams: URLSearchParams,
  values: Partial<PricingCalculatorFormValues>,
): void => {
  // Backward-compat: legacy ?st=1 maps to tenantType=single when tenantType not explicitly set
  if (values.tenantType === undefined) {
    const legacySt = searchParams.get('st');
    if (legacySt === '1' || legacySt === 'true') {
      values.tenantType = TenantType.SINGLE;
    }
  }
  // Backward-compat: legacy ?dst= maps to discountOnTenant when ?dt= not explicitly set
  if (values.discountOnTenant === undefined) {
    const legacyDst = searchParams.get('dst');
    if (legacyDst !== null) {
      const num = parseFloat(legacyDst);
      if (!isNaN(num)) values.discountOnTenant = num;
    }
  }
};

/**
 * Parse URL parameters into form values
 * This enables shareable quote links
 */
const parseUrlParamsToFormValues = (
  searchParams: URLSearchParams,
): Partial<PricingCalculatorFormValues> => {
  const values: Partial<PricingCalculatorFormValues> = {};

  for (const [param, field] of Object.entries(PARAM_MAP)) {
    const rawValue = searchParams.get(param);
    if (rawValue === null) continue;

    applyParsedParam(values, field, rawValue);
  }

  applyLegacyParams(searchParams, values);

  return values;
};

type InitialValuesInputs = {
  hasQuoteParams: boolean;
  urlParamValues: Partial<PricingCalculatorFormValues>;
  companyName?: string;
  memberCategoryCounts?: TeamMemberCategoryCounts;
  teamIdToUse: number | null;
  activeIntegrations: string[];
  tenantType: TenantType;
  isolatedDatabase: boolean;
  teamData?: TeamSearchItem;
};

/**
 * Map team data to form initial values. If we have URL params from a shared link,
 * they take priority.
 */
const buildInitialValues = ({
  hasQuoteParams,
  urlParamValues,
  companyName,
  memberCategoryCounts,
  teamIdToUse,
  activeIntegrations,
  tenantType,
  isolatedDatabase,
  teamData,
}: InitialValuesInputs): Partial<PricingCalculatorFormValues> => {
  if (hasQuoteParams) {
    return {
      // URL params take priority for shared quote links
      ...urlParamValues,
      // But still apply govCloud from environment if not in URL
      govCloud: urlParamValues.govCloud ?? isGovCloud,
    };
  }

  // Default: derive from team data. Counts are split by employment type so
  // each member is billed in exactly one category (GLX-1842). With no team
  // context (e.g. sidebar entry) members default to 10 as a starting quote;
  // for a team whose counts could not be loaded we default to 0 rather than
  // fabricating 10 members for a real team.
  return {
    companyName: companyName || '',
    numberOfMembers: memberCategoryCounts?.basicMember ?? (teamIdToUse ? 0 : 10),
    numberOfInternalContractors: memberCategoryCounts?.internalContractor ?? 0,
    numberOfExternalContractors: memberCategoryCounts?.externalContractor ?? 0,
    numberOfGuests: memberCategoryCounts?.guest ?? 0,
    selectedIntegrations: activeIntegrations,
    isRenewal: companyName ? true : false,
    govCloud: isGovCloud,
    tenantType,
    isolatedDatabase,
    // SSO add-on: enable if team already has SSO enabled
    securityPackageSso: teamData?.ssoEnabled ?? false,
  };
};

/**
 * PricingCalculatorPage
 *
 * Page component that wraps the PricingCalculatorForm with team-specific data.
 * Loads team information and passes it as initial values to the form.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - Form component
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Type definitions
 */
export const PricingCalculatorPage = () => {
  const [searchParams] = useSearchParams();
  const { tenantsHash, tenantOrder } = useTenants({ isOff: false });
  const teamId = useSearchParam('team_id');
  const teamIdToUse = teamId ? +teamId : null;

  // Get tenant/realm from URL params or global state for API calls
  const { tenant, realmId } = useNavigationContext();

  // Parse URL parameters for shareable links
  // URL params take priority over team-derived values
  const urlParamValues = useMemo(() => parseUrlParamsToFormValues(searchParams), [searchParams]);

  // Check if we have significant URL params (more than just team_id, tenant, realmId)
  // This indicates a shared quote link
  const hasQuoteParams = useMemo(() => {
    // `st` retained for legacy shared-quote links that predate the tenantType refactor
    const quoteParamKeys = ['cn', 'nm', 'pm', 'sd', 'pt', 'tt', 'st', 'gc', 'int'];
    return quoteParamKeys.some((key) => searchParams.has(key));
  }, [searchParams]);

  // Check if tenant data has loaded - tenantOrder will be populated after fetch
  // If tenant feature is disabled, we don't need to wait for tenant data
  const isTenantFeatureEnabled = getIsTenantEnabled();
  const isTenantDataLoaded = !isTenantFeatureEnabled || (tenantOrder?.length ?? 0) > 0;

  // Determine tenantType and isolatedDatabase from URL params
  // Find the tenant object by tenantName from URL
  const tenantId = tenantOrder?.find((tid) => tenantsHash[tid]?.tenantName === tenant);
  const tenantObj = tenantId ? tenantsHash[tenantId] : undefined;

  // tenantType: always default to SHARED — user must explicitly opt into a
  // dedicated tier. Previously derived from tenant.isDefault but that caused
  // unexpected behavior.
  const tenantType = TenantType.SHARED;

  // isolatedDatabase: true if realmId from URL doesn't match tenant's primaryRealm
  // When primaryRealm is null/undefined, we cannot determine isolation status, so default to false
  const isolatedDatabase =
    tenantObj && realmId && tenantObj.primaryRealm ? realmId !== tenantObj.primaryRealm : false;

  const {
    getRetoolIntegrationList,
    isFetchingIntegrationForRetool: isLoading,
    companyName,
  } = useTeamRetoolIntegrations({ teamId: teamIdToUse, tenant, realmId });

  // Member counts split by employment type (member / internal contractor /
  // external contractor / guest). This is the only per-team source that exposes
  // the internal-contractor count, and its categories are mutually exclusive so
  // they prefill the calculator without double-counting contractors (GLX-1842).
  const { memberCategoryCounts, isLoadingMemberCategoryCounts } = useTeamMemberCategoryCountsQuery({
    teamId: teamIdToUse || undefined,
    tenant,
    realmId,
    enabled: !!teamIdToUse,
  });

  // Fetch team data from Galaxy API to get SSO status
  // This is a fast API call that provides additional team metadata
  const { teamsSearch, isLoading: isLoadingTeamSearch } = useSearchTeamsByFieldsQuery({
    tenant,
    realmId,
    teamId: teamIdToUse?.toString(),
    enabled: !!teamIdToUse && !!tenant && !!realmId,
    limit: 1,
  });

  const teamData = useMemo(() => {
    return teamsSearch?.data?.[0];
  }, [teamsSearch?.data]);

  // Wait for tenant data, team integrations, AND the member category counts to
  // load before rendering the form. This prevents a race where the form
  // initializes with incorrect tenantType/isolatedDatabase values or with member
  // counts that have not resolved yet.
  if (isLoading || !isTenantDataLoaded || isLoadingTeamSearch || isLoadingMemberCategoryCounts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearBuffer />
      </Box>
    );
  }

  const activeIntegrations = getRetoolIntegrationList();

  const initialValues = buildInitialValues({
    hasQuoteParams,
    urlParamValues,
    companyName,
    memberCategoryCounts,
    teamIdToUse,
    activeIntegrations,
    tenantType,
    isolatedDatabase,
    teamData,
  });

  // Always render the form - even if data failed to load
  // This allows users to use the calculator with manual input
  return <PricingCalculatorFormV2 initialValues={initialValues} />;
};
